import React from 'react';
import { MetricCard } from '@/components/dashboard/MetricCard';
import { TierDistributionChart } from '@/components/dashboard/TierDistributionChart';
import { ActivityFeed } from '@/components/dashboard/ActivityFeed';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { useMetrics, useRecentActivity, useCostAnalysis, useCacheStats } from '@/hooks/useAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, PieChart, Pie } from 'recharts';
import { getTierColor, getTierName, formatCost } from '@/lib/utils';
import { Cpu, Cloud, TrendingDown, Database, Zap, Trash2 } from 'lucide-react';

export function DashboardPage() {
  const { metrics, loading: metricsLoading } = useMetrics(5000);
  const { activities, loading: activitiesLoading } = useRecentActivity(10, 10000);
  const { analysis: costAnalysis } = useCostAnalysis(7);
  const { cacheStats, clearCache } = useCacheStats(5000);

  // Prepare cost chart data
  const costChartData = costAnalysis?.tier_costs
    ? Object.entries(costAnalysis.tier_costs).map(([tier, cost]) => ({
        tier,
        name: getTierName(tier),
        cost,
        color: getTierColor(tier),
      }))
    : [];

  // Prepare cache hit/miss pie chart data
  const cacheChartData = cacheStats ? [
    { name: 'Hits', value: cacheStats.hits || 0, color: '#10b981' },
    { name: 'Misses', value: cacheStats.misses || 0, color: '#6366f1' },
  ] : [];

  const handleClearCache = async () => {
    if (window.confirm('Clear semantic cache? This will remove all cached responses.')) {
      await clearCache();
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">
            INFERENCE DASHBOARD
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitor your tiered inference pipeline in real-time
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/20">
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-mono text-emerald-400">SYSTEM HEALTHY</span>
          </div>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Requests"
          value={metrics?.total_requests || 0}
          subtitle="All time"
          icon="requests"
          color="primary"
        />
        <MetricCard
          title="Avg Latency"
          value={metrics?.average_latency_ms || 0}
          subtitle="Across all tiers"
          icon="latency"
          color="amber"
          format="latency"
        />
        <MetricCard
          title="Cache Hit Rate"
          value={metrics?.cache_hit_ratio || 0}
          subtitle="Semantic cache efficiency"
          icon="cache"
          color="emerald"
          format="percent"
        />
        <MetricCard
          title="Cost Saved"
          value={metrics?.total_cost_saved || 0}
          subtitle="vs. cloud-only"
          icon="savings"
          color="violet"
          format="cost"
        />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Interface - Spans 2 columns */}
        <div className="lg:col-span-2">
          <ChatInterface />
        </div>

        {/* Right Column - Tier Distribution & Activity */}
        <div className="space-y-6">
          <TierDistributionChart data={metrics?.tier_distribution} />
          
          {/* Cost by Tier */}
          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-emerald-400" />
                Cost by Tier (7 days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {costChartData.length > 0 ? (
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={costChartData} layout="vertical">
                      <XAxis type="number" hide />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={80}
                        tick={{ fontSize: 10, fill: '#a1a1aa' }}
                      />
                      <Tooltip
                        formatter={(value) => [formatCost(value), 'Cost']}
                        contentStyle={{
                          backgroundColor: '#18181b',
                          border: '1px solid #27272a',
                          borderRadius: '6px',
                        }}
                      />
                      <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
                        {costChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                  No cost data available
                </div>
              )}
              {costAnalysis && (
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Total Savings</span>
                  <span className="text-sm font-mono font-medium text-emerald-400">
                    {costAnalysis.savings_percentage}% ({formatCost(costAnalysis.savings)})
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Semantic Cache Stats */}
          <Card className="border-border bg-card" data-testid="cache-stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-cyan-400" />
                  Semantic Cache
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleClearCache}
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
                  data-testid="clear-cache-btn"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {/* Pie Chart */}
                <div className="h-20 w-20 flex-shrink-0">
                  {cacheChartData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={cacheChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={20}
                          outerRadius={35}
                          paddingAngle={2}
                        >
                          {cacheChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <div className="h-16 w-16 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                        <span className="text-[10px] text-muted-foreground">No data</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* Stats */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                      <span className="text-xs text-muted-foreground">Hit Rate</span>
                    </div>
                    <span className="font-mono text-sm text-emerald-400">
                      {cacheStats?.hit_rate?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-3 w-3 text-amber-400" />
                      <span className="text-xs text-muted-foreground">Entries</span>
                    </div>
                    <span className="font-mono text-sm">
                      {cacheStats?.cache_size?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Lookups</span>
                    <span className="font-mono text-xs">
                      {cacheStats?.total_lookups?.toLocaleString() || 0}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Similarity Threshold</span>
                  <span className="font-mono">{cacheStats?.similarity_threshold || 0.85}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Activity Feed - Full Width */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ActivityFeed activities={activities} loading={activitiesLoading} />
        </div>
        
        {/* Quick Stats */}
        <div className="space-y-4">
          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Routing Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-md bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-emerald-400" />
                  <span className="text-sm">Local Processing</span>
                </div>
                <span className="font-mono text-emerald-400">
                  {metrics?.tier_distribution ? 
                    ((metrics.tier_distribution.tier1_local_fast?.percentage || 0) + 
                     (metrics.tier_distribution.tier2_local_smart?.percentage || 0)).toFixed(1)
                    : 0}%
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-md bg-violet-500/10 border border-violet-500/20">
                <div className="flex items-center gap-2">
                  <Cloud className="h-4 w-4 text-violet-400" />
                  <span className="text-sm">Cloud Escalation</span>
                </div>
                <span className="font-mono text-violet-400">
                  {metrics?.tier_distribution?.tier3_cloud?.percentage || 0}%
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">System Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Uptime</span>
                  <span className="font-mono">
                    {metrics?.uptime_seconds
                      ? `${Math.floor(metrics.uptime_seconds / 3600)}h ${Math.floor((metrics.uptime_seconds % 3600) / 60)}m`
                      : '--'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">PII Detections</span>
                  <span className="font-mono">{metrics?.pii_detections || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Tokens</span>
                  <span className="font-mono">{(metrics?.total_tokens || 0).toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
